import _ from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ErrorMesssage from 'v2/common/components/ErrorMessage/index';
import { Controller } from 'react-hook-form';
import { outputSchemaPropTypes, rightOperandPropTypes } from 'common/propTypes';
import { OutputSchemaUtility, TypeheadSchemaUtility } from 'v2/common/quantConfig/outputSchema/utility';
import { Menu, MenuItem } from 'react-bootstrap-typeahead';
import ReactHtmlParser from 'react-html-parser';
import CustomTypeAhead from 'v3/common/components/CustomTypeAhead';

const propTypes = {
  rightOperand: rightOperandPropTypes.isRequired,
  onChangeRightOperand: PropTypes.func.isRequired,
  selected: PropTypes.arrayOf(PropTypes.shape({})),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  isAllowNew: PropTypes.bool.isRequired,
  outputSchema: outputSchemaPropTypes.isRequired,
};

const defaultProps = {
  selected: [],
  options: [],
};

const Default = ({
  onChangeRightOperand,
  selected,
  options,
  control,
  errors,
  isAllowNew,
  outputSchema,
  rightOperand,
}) => {
  const typeaheadRef = useRef(null);
  const uniqueId = useMemo(() => _.uniqueId('right-operand-'), []);
  const errorMessage = useMemo(
    () => (_.isEmpty(rightOperand) ? _.get(errors, `${uniqueId}.message`, '') : ''),
    [rightOperand, errors, uniqueId]
  );
  const selectedRightOperandSchema = useMemo(() => {
    return OutputSchemaUtility.findByName(outputSchema, rightOperand);
  }, [outputSchema, rightOperand]);

  const validator = {
    validate: (value) => {
      if (_.isArray(value) && _.isEmpty(value[0]?.searchLabel)) return 'Please select a Right operand';

      return null;
    },
  };

  const onChangeOrOnBlur = (result, onChange) => {
    if (_.isEmpty(result)) return;
    const { id, customOption, searchLabel } = result[0];

    onChange(result);
    if (customOption) {
      const newLabel = Number(searchLabel);
      if (Number.isNaN(newLabel)) {
        onChangeRightOperand('');
        return;
      }
      onChangeRightOperand(searchLabel);
    } else {
      onChangeRightOperand(id);
    }
  };

  const onBlur = (event, onChange) => {
    const result = event.target.value;
    const newLabel = Number(result);
    if (!Number.isNaN(newLabel) && isAllowNew) {
      onChangeOrOnBlur([{ customOption: true, searchLabel: result, id: _.uniqueId('new-id-') }], onChange);
    }
  };

  const isNumber = useCallback((label) => {
    return !_.isNaN(Number(label));
  }, []);

  const getPriority = useCallback(
    (result) => {
      return isNumber(result.searchLabel) ? 1 : 2;
    },
    [isNumber]
  );

  return (
    <ErrorMesssage message={errorMessage}>
      <Controller
        render={({ onChange, name }) => (
          <CustomTypeAhead
            customKey={JSON.stringify(selected)}
            className="custom-form-control"
            disabled={false}
            name={name}
            labelKey="searchLabel"
            allowNew={isAllowNew}
            ref={typeaheadRef}
            options={_.uniqBy(_.flattenDeep([options || [], selected || []]), 'searchLabel')}
            defaultSelected={selected}
            clearButton={!_.isEmpty(selected)}
            onBlur={(event) => onBlur(event, onChange)}
            onChange={(result) => {
              if (result.length === 0 && typeaheadRef.current) {
                typeaheadRef.current?.focus();
                typeaheadRef.current?.toggleMenu();
              }
              onChangeOrOnBlur(result, onChange);
            }}
            filterBy={(option, filterProps) =>
              TypeheadSchemaUtility.getOptionFilteredBySearchText(option, filterProps)
            }
            customRenderMenuItem={(results, menuProps) => {
              const omittedProps = _.omit(menuProps, [
                    'newSelectionPrefix',
                    'paginationText',
                    'renderMenuItemChildren',
                  ]);
              const remainingItems = options.filter((opt) => !results.includes(opt));
              const priorityResult = _.chain(results)
                .filter(
                  (result) => !result.customOption || (result.customOption && isNumber(result.searchLabel))
                )
                .map((result) => {
                  return {
                    ...result,
                    priority: getPriority(result),
                  };
                })
                .sortBy('priority')
                .value();
              const renderMenuItem = (renderResults, index) => (
                <MenuItem option={renderResults} position={index} key={index}>
                  {ReactHtmlParser(
                    renderResults.customOption ? renderResults.searchLabel : renderResults.label
                  )}
                </MenuItem>
              );

              return (
                <Menu {...omittedProps}>
                  {priorityResult.length > 0 ? (
                    _.map(priorityResult, (result, index) => renderMenuItem(result, index))
                  ) : (
                    <div className="px-2 py-1 text-center text-muted small">
                      <span className="fw-bold">No matches found</span>
                    </div>
                  )}
                  {remainingItems.length > 0 && (
                    <>
                      <div className="instrument-custom-dropdown">
                        <span>More Options</span>
                      </div>
                      {_.map(remainingItems, (result, index) =>
                        renderMenuItem(result, index + priorityResult.length)
                      )}
                    </>
                  )}
                </Menu>
              );
            }}
          />
        )}
        control={control}
        defaultValue={selected}
        name={uniqueId}
        rules={validator}
      />
      {selectedRightOperandSchema?.infoLink && (
        <span
          className="material-icons-outlined info-icon ml-1"
          onClick={() => window.open(selectedRightOperandSchema?.infoLink, '_blank')}
        >
          info
        </span>
      )}
    </ErrorMesssage>
  );
};

Default.propTypes = propTypes;
Default.defaultProps = defaultProps;

export default Default;
